import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import Header from '../../components/Header';
import { tokens } from '../../theme';
import ModalCliente from '../../modal/cliente/modalCliente';
import { useLocation } from 'react-router-dom';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { exportarInformeLaboratorio } from '../../utils/labPdfExport';
import {
  cerrarEnsayoLaboratorio,
  createEnsayoLaboratorio,
  getCatalogoPruebasLaboratorio,
  getChapaContextoLaboratorio,
  getDatosSerieLaboratorio,
  getEnsayoLaboratorioById,
  getEnsayosLaboratorio,
  getResultadosVistaEnsayoLaboratorio,
  guardarBloqueEnsayoLaboratorio,
  seleccionarPruebasEnsayoLaboratorio,
} from '../../services/laboratorio.services';

const UI_BLOCKS = [
  {
    codigo: 'VISUAL_BORDES',
    nombre: 'VERIFICACION DE ASPECTO VISUAL DE LAS BORDAS',
    columnas: ['identificacion', 'aspecto_visual', 'bordas'],
    opciones: {
      aspecto_visual: ['ok', 'no ok'],
      bordas: ['ok', 'no ok'],
    },
  },
  {
    codigo: 'ESPESURA',
    nombre: 'VERIFICACION DE ESPESURA',
    columnas: ['identificacion', 'espesor_nominal', 'espesor_medida', 'resultado'],
  },
  {
    codigo: 'TEST_BAKE',
    nombre: 'ENSAYOS ESPECIFICOS - TEST BAKE',
    columnas: ['identificacion', 'burbujas', 'nubosidad', 'resultado'],
  },
  {
    codigo: 'HUMEDAD_A',
    nombre: 'TEST DE RESISTENCIA A HUMEDAD POR CONDENSACION (A)',
    columnas: ['identificacion', 'burbujas', 'resultado'],
    restriccion: '14 dias de espera',
  },
  {
    codigo: 'HUMEDAD_B',
    nombre: 'TEST DE RESISTENCIA A HUMEDAD SIN CONDENSACION (B)',
    columnas: ['identificacion', 'burbujas', 'nubosidad', 'resultado'],
  },
  {
    codigo: 'PUMMEL',
    nombre: 'PUMMEL',
    columnas: ['identificacion', 'pummel', 'resultado'],
  },
  {
    codigo: 'IMPACTO_PENDULO',
    nombre: 'TEST DE IMPACTO PENDULO',
    columnas: ['identificacion', 'resistencia_impacto', 'resultado'],
  },
  {
    codigo: 'BALL_DROP',
    nombre: 'BALL DROP',
    columnas: ['identificacion', 'resistencia', 'resultado'],
  },
  {
    codigo: 'ANALISIS_PRODUCCION',
    nombre: 'ANALISIS DURANTE LA PRODUCCION',
    columnas: ['ensayo', 'identificacion', 'mayor_resultado', 'maximo_permitido', 'resultado'],
  },
];

const FIELD_LABELS = {
  identificacion: 'Identificacion',
  aspecto_visual: 'Aspecto visual',
  bordas: 'Bordas',
  espesor_nominal: 'Espesor nominal',
  espesor_medida: 'Espesor medida',
  resultado: 'Resultado',
  burbujas: 'Burbujas',
  nubosidad: 'Nubosidad',
  pummel: 'Pummel',
  resistencia_impacto: 'Resistencia a impacto',
  resistencia: 'Resistencia',
  ensayo: 'Ensayo',
  mayor_resultado: 'Mayor resultado',
  maximo_permitido: 'Maximo permitido',
};

const normalizeText = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const matchesAny = (value, patterns) => {
  const normalized = normalizeText(value);
  return patterns.some((pattern) => normalized.includes(normalizeText(pattern)));
};

const parseIdentifierToken = (value) => {
  const raw = String(value || '').trim();

  if (!raw) {
    return null;
  }

  const match = raw.match(/^([A-Za-z]*)(\d+)$/);

  if (!match) {
    return { prefix: '', number: null, width: 0, raw };
  }

  return {
    prefix: match[1] || '',
    number: Number(match[2]),
    width: match[2].length,
    raw,
  };
};

const expandIdentifiers = (prueba) => {
  const start = parseIdentifierToken(prueba.identificador_inicio);
  const end = parseIdentifierToken(prueba.identificador_fin);
  const count = Number(prueba.cantidad_muestras) || 0;

  if (start?.number != null && end?.number != null && start.prefix === end.prefix && end.number >= start.number) {
    return Array.from({ length: end.number - start.number + 1 }, (_, index) => {
      const current = start.number + index;
      return `${start.prefix}${String(current).padStart(start.width, '0')}`;
    });
  }

  if (start?.raw && count > 1) {
    return Array.from({ length: count }, (_, index) => `${start.raw}-${index + 1}`);
  }

  if (start?.raw) {
    return [start.raw];
  }

  return Array.from({ length: Math.max(count, 1) }, (_, index) => `${prueba.codigo || 'MUESTRA'}-${index + 1}`);
};

const buildSampleRows = (pruebas) =>
  pruebas.flatMap((prueba) =>
    expandIdentifiers(prueba).map((identificacion, index) => ({
      rowId: `${prueba.id}-${identificacion}-${index}`,
      pruebaId: prueba.id,
      pruebaCodigo: prueba.codigo,
      pruebaNombre: prueba.nombre,
      grupo: prueba.grupo,
      subtipo: prueba.subtipo,
      dimensiones: prueba.dimensiones,
      dias_espera: prueba.dias_espera,
      identificacion,
    }))
  );

const formatDate = (value) => {
  if (!value || !dayjs(value).isValid()) {
    return '-';
  }

  return dayjs(value).format('DD/MM/YYYY HH:mm');
};

const getContextField = (contexto, keys) => {
  for (const key of keys) {
    if (contexto?.[key] != null && contexto[key] !== '') {
      return contexto[key];
    }
  }

  return '';
};

const parsePvbThickness = (producto) => {
  const pvbMatch = String(producto || '').match(/(?:arq\.?|pvb\s*|pvbinc\s*)(\d+(?:[.,]\d+)?)/i);

  if (!pvbMatch) {
    return 0;
  }

  let value = Number(String(pvbMatch[1]).replace(',', '.'));

  if (Number.isNaN(value)) {
    return 0;
  }

  if (value >= 10) {
    value = value / 100;
  }

  return value;
};

const parseGlassThickness = (producto) => {
  const normalized = String(producto || '').toLowerCase();
  const plusMatch = normalized.match(/(\d+(?:[.,]\d+)?)\s*\+\s*(\d+(?:[.,]\d+)?)/);

  if (plusMatch) {
    return Number(plusMatch[1].replace(',', '.')) + Number(plusMatch[2].replace(',', '.'));
  }

  const incMatch = normalized.match(/inc(?:\+inc)?\s*(\d{3,4})/);

  if (incMatch) {
    const token = incMatch[1];

    if (token.length === 3) {
      return Number(token[0]) + Number(token[2]);
    }

    if (token.length === 4) {
      return Number(token.slice(0, 2)) + Number(token.slice(2));
    }
  }

  return 0;
};

const calculateNominalThickness = (producto) => {
  const glass = parseGlassThickness(producto);
  const pvb = parsePvbThickness(producto);
  const total = glass + pvb;

  if (!total) {
    return '';
  }

  return total.toFixed(2);
};

const getPruebaBlockKey = (prueba) => {
  const codigo = prueba.codigo || prueba.pruebaCodigo || '';
  const nombre = prueba.nombre || prueba.pruebaNombre || '';
  const base = `${codigo} ${nombre} ${prueba.grupo || ''} ${prueba.subtipo || ''}`;
  const normalized = normalizeText(base);

  if (matchesAny(base, ['ball drop'])) {
    return 'BALL_DROP';
  }

  if (matchesAny(base, ['pendulo'])) {
    return 'IMPACTO_PENDULO';
  }

  if (matchesAny(base, ['bake', 'alta temperatura'])) {
    return 'TEST_BAKE';
  }

  if (matchesAny(base, ['umidade', 'humedad'])) {
    if (
      normalized.includes('condensacion') ||
      normalized.includes('(a)') ||
      normalized.includes(' subtipo a') ||
      normalized.endsWith(' a')
    ) {
      return 'HUMEDAD_A';
    }

    if (
      normalized.includes('sin condensacion') ||
      normalized.includes('(b)') ||
      normalized.includes(' subtipo b') ||
      normalized.endsWith(' b')
    ) {
      return 'HUMEDAD_B';
    }
  }

  if (matchesAny(base, ['pummel', 'adesao'])) {
    return 'PUMMEL';
  }

  return null;
};

const getTargetRowsByBlock = (sampleRows, pruebas) => {
  const map = {
    VISUAL_BORDES: sampleRows,
    ESPESURA: sampleRows,
    ANALISIS_PRODUCCION: pruebas.map((prueba) => ({
      rowId: `ANALISIS-${prueba.id}`,
      ensayo: prueba.nombre,
      identificacion: expandIdentifiers(prueba).join(', '),
      pruebaId: prueba.id,
    })),
  };

  sampleRows.forEach((row) => {
    const blockCode = getPruebaBlockKey(row);

    if (!blockCode) {
      return;
    }

    if (!map[blockCode]) {
      map[blockCode] = [];
    }

    map[blockCode].push(row);
  });

  return map;
};

const getStoredRowsForBlock = (existingResults, blockCode) => {
  if (!Array.isArray(existingResults)) {
    return [];
  }

  const found = existingResults.find((entry) => {
    const code = entry?.codigo || entry?.codigo_bloque || entry?.blockCode || entry?.bloque || entry?.nombre;
    return code === blockCode;
  });

  if (!found) {
    return [];
  }

  if (Array.isArray(found.rows)) {
    return found.rows;
  }

  if (Array.isArray(found.resultados)) {
    return found.resultados;
  }

  if (Array.isArray(found.detalle)) {
    return found.detalle;
  }

  return [];
};

const buildDefaultBlockRow = (blockCode, targetRow, nominalThickness) => {
  if (blockCode === 'ANALISIS_PRODUCCION') {
    return {
      rowId: targetRow.rowId,
      ensayo: targetRow.ensayo,
      identificacion: targetRow.identificacion,
      mayor_resultado: '',
      maximo_permitido: '',
      resultado: '',
    };
  }

  const base = {
    rowId: targetRow.rowId,
    identificacion: targetRow.identificacion,
  };

  if (blockCode === 'ESPESURA') {
    return {
      ...base,
      espesor_nominal: nominalThickness,
      espesor_medida: '',
      resultado: '',
    };
  }

  if (blockCode === 'VISUAL_BORDES') {
    return {
      ...base,
      aspecto_visual: '',
      bordas: '',
    };
  }

  if (blockCode === 'TEST_BAKE') {
    return {
      ...base,
      burbujas: '',
      nubosidad: '',
      resultado: '',
    };
  }

  if (blockCode === 'HUMEDAD_A') {
    return {
      ...base,
      burbujas: '',
      resultado: '',
    };
  }

  if (blockCode === 'HUMEDAD_B') {
    return {
      ...base,
      burbujas: '',
      nubosidad: '',
      resultado: '',
    };
  }

  if (blockCode === 'PUMMEL') {
    return {
      ...base,
      pummel: '',
      resultado: '',
    };
  }

  if (blockCode === 'IMPACTO_PENDULO') {
    return {
      ...base,
      resistencia_impacto: '',
      resultado: '',
    };
  }

  if (blockCode === 'BALL_DROP') {
    return {
      ...base,
      resistencia: '',
      resultado: '',
    };
  }

  return base;
};

const reconcileRows = (blockCode, targetRows, storedRows, nominalThickness) =>
  targetRows.map((targetRow) => {
    const stored = storedRows.find((row) => {
      if (blockCode === 'ANALISIS_PRODUCCION') {
        return row?.ensayo === targetRow.ensayo;
      }

      return row?.identificacion === targetRow.identificacion;
    });

    return {
      ...buildDefaultBlockRow(blockCode, targetRow, nominalThickness),
      ...(stored || {}),
      rowId: targetRow.rowId,
      identificacion: targetRow.identificacion,
      ensayo: targetRow.ensayo,
    };
  });

const getResultStatus = (value) => {
  const normalized = normalizeText(value);

  if (!normalized) {
    return 'pendiente';
  }

  if (
    normalized.includes('no ok') ||
    normalized.includes('reprob') ||
    normalized.includes('falha') ||
    normalized.includes('fallo') ||
    normalized.includes('no conforme')
  ) {
    return 'rechazado';
  }

  return 'aprobado';
};

const STATUS_COLORS = {
  aprobado: '#4caf50',
  rechazado: '#f44336',
  pendiente: '#ff9800',
};

const GRUPO_CHIP_COLORS = {
  impacto: { bg: '#ff9800', text: '#000' },
  resistencia: { bg: '#1976d2', text: '#fff' },
  adhesion: { bg: '#7b1fa2', text: '#fff' },
  adesao: { bg: '#7b1fa2', text: '#fff' },
  espesura: { bg: '#0288d1', text: '#fff' },
  visual: { bg: '#388e3c', text: '#fff' },
  produccion: { bg: '#5d4037', text: '#fff' },
};

const getGrupoChipSx = (label) => {
  if (!label) return {};
  const key = normalizeText(label);
  const match = Object.keys(GRUPO_CHIP_COLORS).find((k) => key.includes(k));
  if (!match) return {};
  const { bg, text } = GRUPO_CHIP_COLORS[match];
  return { backgroundColor: bg, color: text, fontWeight: 600 };
};

const getResultCellSx = (value) => {
  const status = getResultStatus(value);
  if (!normalizeText(value)) return {};
  return { color: STATUS_COLORS[status], fontWeight: 700 };
};

const getClienteId = (cliente) =>
  Number(
    cliente?.id_cliente ?? cliente?.id ?? cliente?.cliente_id ?? cliente?.codigo_cliente ?? 0
  ) || null;

const normalizeEnsayosList = (response) => {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  return [];
};

const normalizeBloquesFromDetalle = (detalle) => {
  const bloques = Array.isArray(detalle?.bloques)
    ? detalle.bloques
    : Array.isArray(detalle?.resultados)
      ? detalle.resultados
      : [];

  return bloques.map((bloque) => {
    if (Array.isArray(bloque?.rows)) {
      return bloque;
    }

    if (Array.isArray(bloque?.items)) {
      const grouped = {};

      bloque.items.forEach((item, index) => {
        const rowKey = item?.id_muestra ?? item?.identificacion ?? `row-${index}`;

        if (!grouped[rowKey]) {
          grouped[rowKey] = {
            rowId: String(rowKey),
            identificacion: item?.identificacion || '',
            ensayo: item?.ensayo || '',
          };
        }

        const valueFromItem =
          item?.valor_text ??
          (item?.valor_num != null ? String(item.valor_num) : '') ??
          (item?.valor_bool != null ? String(item.valor_bool) : '');

        if (item?.columna) {
          grouped[rowKey][item.columna] = valueFromItem;
        }

        if (item?.resultado != null && item?.resultado !== '') {
          grouped[rowKey].resultado = item.resultado;
        }
      });

      return {
        ...bloque,
        rows: Object.values(grouped),
      };
    }

    return bloque;
  });
};

const mapResultadosVistaToBloques = (rows = []) => {
  const groupedBlocks = new Map();

  rows.forEach((item, index) => {
    const bloqueCodigo = item?.bloque_codigo || 'SIN_CODIGO';
    const bloqueNombre = item?.bloque_nombre || bloqueCodigo;
    const blockKey = `${bloqueCodigo}__${bloqueNombre}`;

    if (!groupedBlocks.has(blockKey)) {
      groupedBlocks.set(blockKey, {
        codigo: bloqueCodigo,
        nombre: bloqueNombre,
        rowsMap: new Map(),
      });
    }

    const block = groupedBlocks.get(blockKey);
    const rowKey =
      item?.id_muestra ??
      item?.identificacion ??
      (item?.id_item != null ? String(item.id_item) : `row-${index}`);

    if (!block.rowsMap.has(rowKey)) {
      block.rowsMap.set(rowKey, {
        rowId: String(rowKey),
        identificacion: item?.identificacion || '',
      });
    }

    const row = block.rowsMap.get(rowKey);
    const value =
      item?.valor_text ??
      (item?.valor_num != null ? String(item.valor_num) : '') ??
      (item?.valor_bool != null ? String(item.valor_bool) : '');

    if (item?.columna) {
      row[item.columna] = value;
    }

    if (item?.resultado != null && item?.resultado !== '') {
      row.resultado = item.resultado;
    }
  });

  return Array.from(groupedBlocks.values()).map((block) => ({
    codigo: block.codigo,
    nombre: block.nombre,
    rows: Array.from(block.rowsMap.values()),
  }));
};

const inferConclusion = (resumen) => {
  const statuses = resumen.map((row) => normalizeText(row.resultado));

  if (statuses.some((status) => status.includes('no conforme') || status.includes('reprob'))) {
    return 'REPROBADO';
  }

  if (statuses.length > 0 && statuses.every((status) => status.includes('aprobado'))) {
    return 'APROBADO';
  }

  return 'OBSERVADO';
};

const buildBlockItemsPayload = (rows = [], blockCode) => {
  const fieldsByBlock = UI_BLOCKS.find((block) => block.codigo === blockCode)?.columnas || [];
  const editableFields = fieldsByBlock.filter((field) => !['rowId', 'ensayo'].includes(field));

  return rows.flatMap((row) =>
    editableFields
      .filter((field) => field !== 'identificacion' || row.identificacion)
      .map((field) => {
        const rawValue = row[field];
        const textValue = rawValue == null ? '' : String(rawValue);
        const numericValue = Number(String(rawValue).replace(',', '.'));
        const hasNumericValue = textValue !== '' && !Number.isNaN(numericValue);

        return {
          identificacion: row.identificacion || undefined,
          columna: field,
          valor_text: hasNumericValue ? undefined : textValue || undefined,
          valor_num: hasNumericValue ? numericValue : undefined,
          valor_bool:
            typeof rawValue === 'boolean'
              ? rawValue
              : textValue.toLowerCase() === 'true'
                ? true
                : textValue.toLowerCase() === 'false'
                  ? false
                  : undefined,
          resultado: row.resultado || undefined,
        };
      })
  );
};

const LaboratorioResultados = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const location = useLocation();
  const [openModalCliente, setOpenModalCliente] = useState(false);
  const [catalogoPruebas, setCatalogoPruebas] = useState([]);
  const [loadingCatalogo, setLoadingCatalogo] = useState(true);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [idProduccion, setIdProduccion] = useState('');
  const [contextoChapa, setContextoChapa] = useState(null);
  const [loadingContexto, setLoadingContexto] = useState(false);
  const [selectedPruebaIds, setSelectedPruebaIds] = useState([]);
  const [resultadosExistentes, setResultadosExistentes] = useState([]);
  const [resultadosPorBloque, setResultadosPorBloque] = useState({});
  const [ensayoId, setEnsayoId] = useState(null);
  const [ensayoEstado, setEnsayoEstado] = useState('BORRADOR');
  const [saving, setSaving] = useState(false);
  const [retest, setRetest] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, msg: '', severity: 'success' });

  const activeCatalogo = useMemo(
    () => catalogoPruebas.filter((prueba) => Number(prueba.status_active) === 1),
    [catalogoPruebas]
  );

  const pruebasSeleccionadas = useMemo(
    () => catalogoPruebas.filter((prueba) => selectedPruebaIds.includes(prueba.id)),
    [catalogoPruebas, selectedPruebaIds]
  );

  const sampleRows = useMemo(() => buildSampleRows(pruebasSeleccionadas), [pruebasSeleccionadas]);

  const productoDescripcion = getContextField(contextoChapa, ['cod', 'producto', 'producto_codigo', 'descripcion_producto']);
  const nominalThickness = useMemo(() => calculateNominalThickness(productoDescripcion), [productoDescripcion]);

  const targetRowsByBlock = useMemo(
    () => getTargetRowsByBlock(sampleRows, pruebasSeleccionadas),
    [sampleRows, pruebasSeleccionadas]
  );

  const resumenFinal = useMemo(
    () =>
      pruebasSeleccionadas.map((prueba) => {
        const blockCode = getPruebaBlockKey(prueba);
        const blockRows = blockCode ? resultadosPorBloque[blockCode] || [] : [];
        const statuses = blockRows.map((row) => getResultStatus(row.resultado || row.bordas || row.aspecto_visual));
        const rejected = statuses.includes('rechazado');
        const approved = statuses.length > 0 && statuses.every((status) => status === 'aprobado');

        return {
          id: prueba.id,
          ensayo: prueba.nombre,
          identificaciones: expandIdentifiers(prueba).join(', '),
          resultado: rejected ? 'No conforme' : approved ? 'Aprobado' : 'Pendiente',
        };
      }),
    [pruebasSeleccionadas, resultadosPorBloque]
  );

  const refreshCatalogo = async () => {
    setLoadingCatalogo(true);

    try {
      const response = await getCatalogoPruebasLaboratorio(false);
      setCatalogoPruebas(Array.isArray(response) ? response : []);
    } catch (error) {
      setSnackbar({ open: true, msg: error.message, severity: 'error' });
    } finally {
      setLoadingCatalogo(false);
    }
  };

  useEffect(() => {
    refreshCatalogo();
  }, []);

  useEffect(() => {
    const state = location?.state;

    if (!state?.ensayoId) {
      return;
    }

    const autoLoad = async () => {
      setLoadingContexto(true);

      try {
        if (state.id_produccion) {
          setIdProduccion(state.id_produccion);
        }

        if (state.id_cliente || state.cliente_nombre) {
          setSelectedCliente({
            id: state.id_cliente || null,
            descripcion: state.cliente_nombre || '',
          });
        }

        setEnsayoId(state.ensayoId);

        let contexto = null;

        if (state.id_produccion) {
          try {
            contexto = await getChapaContextoLaboratorio(state.id_produccion);
          } catch {
            try {
              contexto = await getDatosSerieLaboratorio(state.id_produccion);
            } catch {
              contexto = { id_produccion: state.id_produccion };
            }
          }
        }

        setContextoChapa({
          ...(contexto || {}),
          id_produccion: state.id_produccion || '',
        });

        await loadEnsayoDetalle(state.ensayoId);
        setSnackbar({ open: true, msg: 'Ensayo cargado correctamente', severity: 'success' });
      } catch (error) {
        setSnackbar({ open: true, msg: error.message || 'No se pudo cargar el ensayo', severity: 'error' });
      } finally {
        setLoadingContexto(false);
      }
    };

    autoLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setResultadosPorBloque((prev) => {
      const nextState = { ...prev };

      UI_BLOCKS.forEach((block) => {
        const targetRows = targetRowsByBlock[block.codigo] || [];
        const storedRows = getStoredRowsForBlock(resultadosExistentes, block.codigo);
        nextState[block.codigo] = reconcileRows(block.codigo, targetRows, storedRows, nominalThickness);
      });

      return nextState;
    });
  }, [targetRowsByBlock, resultadosExistentes, nominalThickness]);

  const loadEnsayoDetalle = async (id) => {
    const detalle = await getEnsayoLaboratorioById(id);
    let normalizedBloques = normalizeBloquesFromDetalle(detalle);

    if (!normalizedBloques.length) {
      try {
        const resultadosVista = await getResultadosVistaEnsayoLaboratorio(id);
        normalizedBloques = mapResultadosVistaToBloques(Array.isArray(resultadosVista) ? resultadosVista : []);
      } catch (error) {
        normalizedBloques = [];
      }
    }

    const pruebasFromDetalle = Array.isArray(detalle?.pruebas) ? detalle.pruebas : [];

    const pruebaIds = pruebasFromDetalle
      .map((prueba) =>
        Number(
          prueba?.id_prueba_catalogo ??
            prueba?.prueba_catalogo_id ??
            prueba?.id_prueba ??
            prueba?.id
        ) || null
      )
      .filter(Boolean);

    setSelectedPruebaIds(pruebaIds);

    setResultadosExistentes(normalizedBloques);
    setEnsayoEstado(detalle?.estado || detalle?.estado_ensayo || 'BORRADOR');
  };

  const resolveOrCreateEnsayo = async (serieValue) => {
    const idCliente = getClienteId(selectedCliente);

    if (!idCliente) {
      throw new Error('No se encontro id de cliente para crear el ensayo');
    }

    const listado = await getEnsayosLaboratorio({
      id_cliente: idCliente,
      id_produccion: serieValue,
      estado: 'BORRADOR',
      page: 1,
      limit: 20,
    });

    const ensayos = normalizeEnsayosList(listado);
    const existente = ensayos[0];

    if (existente?.id) {
      setEnsayoId(existente.id);
      await loadEnsayoDetalle(existente.id);
      return existente.id;
    }

    const nuevo = await createEnsayoLaboratorio({
      id_cliente: idCliente,
      id_produccion: serieValue,
      observacion: '',
    });

    if (!nuevo?.id) {
      throw new Error('No se pudo crear el ensayo en estado BORRADOR');
    }

    setEnsayoId(nuevo.id);
    setEnsayoEstado(nuevo?.estado || 'BORRADOR');
    setResultadosExistentes([]);
    return nuevo.id;
  };

  const handleLoadContexto = async () => {
    if (!selectedCliente) {
      setSnackbar({ open: true, msg: 'Seleccione un cliente antes de cargar la chapa', severity: 'warning' });
      return;
    }

    if (!idProduccion.trim()) {
      setSnackbar({ open: true, msg: 'Ingrese un ID de produccion', severity: 'warning' });
      return;
    }

    setLoadingContexto(true);

    try {
      let contexto;

      try {
        contexto = await getChapaContextoLaboratorio(idProduccion.trim());
      } catch (error) {
        contexto = await getDatosSerieLaboratorio(idProduccion.trim());
      }

      const normalizedContexto = {
        ...contexto,
        id_produccion: getContextField(contexto, ['id_produccion', 'serie']) || idProduccion.trim(),
      };

      setContextoChapa(normalizedContexto);
      await resolveOrCreateEnsayo(idProduccion.trim());

      setSnackbar({ open: true, msg: 'Contexto de chapa y ensayo cargados', severity: 'success' });
    } catch (error) {
      setContextoChapa(null);
      setResultadosExistentes([]);
      setSelectedPruebaIds([]);
      setEnsayoId(null);
      setSnackbar({ open: true, msg: error.message || 'No se pudo cargar la chapa', severity: 'error' });
    } finally {
      setLoadingContexto(false);
    }
  };

  const handleTogglePrueba = (pruebaId) => {
    setSelectedPruebaIds((prev) =>
      prev.includes(pruebaId) ? prev.filter((id) => id !== pruebaId) : [...prev, pruebaId]
    );
  };

  const handleBlockCellChange = (blockCode, rowId, field, value) => {
    setResultadosPorBloque((prev) => ({
      ...prev,
      [blockCode]: (prev[blockCode] || []).map((row) =>
        row.rowId === rowId ? { ...row, [field]: value } : row
      ),
    }));
  };

  const handleExportarPdf = () => {
    try {
      exportarInformeLaboratorio({
        cliente: selectedCliente?.descripcion || selectedCliente?.nombre || '',
        idProduccion,
        contextoChapa,
        pruebasSeleccionadas,
        resultadosPorBloque,
        resumenFinal,
        ensayoId,
        nominalThickness,
      });
    } catch (error) {
      setSnackbar({ open: true, msg: `Error al exportar PDF: ${error.message}`, severity: 'error' });
    }
  };

  const handleGuardarResultados = async () => {
    if (!selectedCliente) {
      setSnackbar({ open: true, msg: 'Seleccione un cliente', severity: 'warning' });
      return;
    }

    if (!contextoChapa) {
      setSnackbar({ open: true, msg: 'Cargue primero el contexto de la chapa', severity: 'warning' });
      return;
    }

    if (!pruebasSeleccionadas.length) {
      setSnackbar({ open: true, msg: 'Seleccione al menos un ensayo', severity: 'warning' });
      return;
    }

    if (!ensayoId) {
      setSnackbar({ open: true, msg: 'Primero cargue el contexto para crear el ensayo', severity: 'warning' });
      return;
    }

    setSaving(true);

    try {
      await seleccionarPruebasEnsayoLaboratorio(ensayoId, selectedPruebaIds);

      const blocksToSave = UI_BLOCKS
        .map((block, index) => {
          const rows = resultadosPorBloque[block.codigo] || [];
          const items = buildBlockItemsPayload(rows, block.codigo).filter(
            (item) => item.valor_text != null || item.valor_num != null || item.valor_bool != null || item.resultado
          );

          if (!items.length) {
            return null;
          }

          return {
            bloque_codigo: block.codigo,
            bloque_nombre: block.nombre,
            orden: index + 1,
            items,
          };
        })
        .filter(Boolean);

      for (const blockPayload of blocksToSave) {
        await guardarBloqueEnsayoLaboratorio(ensayoId, blockPayload);
      }

      const conclusion = inferConclusion(resumenFinal);
      const comentario = retest ? 'Cierre realizado con marca de retest' : '';

      await cerrarEnsayoLaboratorio(ensayoId, {
        conclusion,
        comentario,
      });

      await loadEnsayoDetalle(ensayoId);
      setEnsayoEstado('FINALIZADO');

      setSnackbar({ open: true, msg: 'Ensayo guardado y cerrado correctamente', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, msg: error.message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const renderCellField = (blockCode, row, field, options) => {
    const value = row[field] ?? '';
    const isReadOnly = field === 'identificacion' || field === 'ensayo' || field === 'espesor_nominal';
    const isResultField = field === 'resultado' || field === 'aspecto_visual' || field === 'bordas';

    if (isReadOnly) {
      return <Typography variant="body2">{value || '-'}</Typography>;
    }

    if (options?.length) {
      return (
        <TextField
          select
          size="small"
          fullWidth
          value={value}
          onChange={(event) => handleBlockCellChange(blockCode, row.rowId, field, event.target.value)}
          sx={isResultField && value ? { '& .MuiInputBase-input': getResultCellSx(value) } : {}}
        >
          <MenuItem value="">Seleccione</MenuItem>
          {options.map((option) => (
            <MenuItem key={option} value={option} sx={isResultField ? getResultCellSx(option) : {}}>
              {option}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    return (
      <TextField
        size="small"
        fullWidth
        value={value}
        onChange={(event) => handleBlockCellChange(blockCode, row.rowId, field, event.target.value)}
        sx={isResultField && value ? { '& .MuiInputBase-input': getResultCellSx(value) } : {}}
      />
    );
  };

  return (
    <Box m="20px">
      <Header title="Resultados de Laboratorio" subtitle="Cliente, chapa, ensayos y captura por bloques" />

      <Grid container spacing={2} mt={1}>
        <Grid item xs={12} md={4}>
          <Box display="flex" flexDirection="column" gap={1}>
            <TextField
              fullWidth
              label="Cliente"
              value={
                selectedCliente
                  ? selectedCliente.descripcion || selectedCliente.nombre || selectedCliente.cliente || selectedCliente.id
                  : ''
              }
              placeholder="Seleccionar cliente..."
              onClick={() => setOpenModalCliente(true)}
              helperText="Seleccione el cliente al que se enviara la prueba"
              InputProps={{
                readOnly: true,
                sx: { cursor: 'pointer' },
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="ID Producción"
            value={idProduccion}
            onChange={(event) => setIdProduccion(event.target.value)}
            helperText="Ingrese el ID de produccion para cargar producto y trazabilidad"
          />
        </Grid>
        <Grid item xs={12} md={4} display="flex" alignItems="center" gap={2}>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleLoadContexto}
            disabled={loadingContexto}
            sx={{ backgroundColor: '#cedc00', color: '#000000', minWidth: 180 }}
          >
            {loadingContexto ? 'Cargando...' : 'Cargar contexto'}
          </Button>
          <FormControlLabel
            control={<Checkbox checked={retest} onChange={(event) => setRetest(event.target.checked)} />}
            label="Retest"
          />
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2, backgroundColor: colors.primary[400] }}>
            {loadingContexto && <CircularProgress size={24} />}
            {!loadingContexto && !contextoChapa && (
              <Typography variant="body2">Todavia no se cargo una chapa.</Typography>
            )}
            {!loadingContexto && contextoChapa && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" mb={1}>Producto</Typography>
                  <Typography variant="body2">ID Producción: {getContextField(contextoChapa, ['id_produccion', 'serie']) || '-'}</Typography>
                  <Typography variant="body2">Codigo: {getContextField(contextoChapa, ['cod', 'producto']) || '-'}</Typography>
                  <Typography variant="body2">Clasificacion: {getContextField(contextoChapa, ['clasificacion', 'id_clasificacion']) || '-'}</Typography>
                  <Typography variant="body2">Cliente seleccionado: {selectedCliente?.descripcion || selectedCliente?.nombre || selectedCliente?.id || '-'}</Typography>
                  <Typography variant="body2">Ensayo ID: {ensayoId || '-'}</Typography>
                  <Typography variant="body2">Estado ensayo: {ensayoEstado || '-'}</Typography>
                  <Typography variant="body2">Espesor nominal calculado: {nominalThickness || '-'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" mb={1}>Trazabilidad</Typography>
                  <Typography variant="body2">Orden: {getContextField(contextoChapa, ['orden', 'id_orden_produccion']) || '-'}</Typography>
                  <Typography variant="body2">Hora calandra: {formatDate(getContextField(contextoChapa, ['HoraCalandra']))}</Typography>
                  <Typography variant="body2">Hora sala limpia: {formatDate(getContextField(contextoChapa, ['HoraSalaLimpia']))}</Typography>
                  <Typography variant="body2">PVB: {getContextField(contextoChapa, ['PVB']) || '-'}</Typography>
                  <Typography variant="body2">Receta: {getContextField(contextoChapa, ['recetaAutoClave']) || '-'}</Typography>
                </Grid>
              </Grid>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Ensayos disponibles</Typography>
              {loadingCatalogo && <CircularProgress size={22} />}
            </Box>
            {!loadingCatalogo && !activeCatalogo.length && (
              <Typography variant="body2">No hay ensayos cargados en el catalogo.</Typography>
            )}
            <Grid container spacing={1}>
              {activeCatalogo
                .filter((prueba) => Number(prueba.status_active) === 1)
                .map((prueba) => {
                  const checked = selectedPruebaIds.includes(prueba.id);
                  return (
                    <Grid item xs={12} md={6} lg={4} key={prueba.id}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderColor: checked ? '#cedc00' : 'divider',
                          borderWidth: checked ? 2 : 1,
                          backgroundColor: checked ? colors.primary[450] || colors.primary[400] : 'transparent',
                          boxShadow: checked ? '0 0 8px 2px rgba(206,220,0,0.25)' : 'none',
                          transition: 'all 0.2s',
                        }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                          <Box>
                            <Typography variant="subtitle1">{prueba.nombre}</Typography>
                            <Typography variant="body2">Codigo: {prueba.codigo}</Typography>
                            <Typography variant="body2">Cantidad: {prueba.cantidad_muestras}</Typography>
                            <Typography variant="body2">Dimensiones: {prueba.dimensiones || '-'}</Typography>
                            <Typography variant="body2">
                              Identificadores: {prueba.identificador_inicio || '-'} a {prueba.identificador_fin || '-'}
                            </Typography>
                          </Box>
                          <Checkbox checked={checked} onChange={() => handleTogglePrueba(prueba.id)} />
                        </Box>
                        <Box mt={1} display="flex" gap={1} flexWrap="wrap">
                          {prueba.grupo && <Chip size="small" label={prueba.grupo} sx={getGrupoChipSx(prueba.grupo)} />}
                          {prueba.subtipo && <Chip size="small" label={prueba.subtipo} sx={{ backgroundColor: '#546e7a', color: '#fff' }} />}
                          {prueba.dias_espera ? <Chip size="small" label={`${prueba.dias_espera} dias`} sx={{ backgroundColor: '#e65100', color: '#fff' }} /> : null}
                        </Box>
                      </Paper>
                    </Grid>
                  );
                })}
            </Grid>
          </Paper>
        </Grid>
        {pruebasSeleccionadas.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" mb={2}>Bloques de resultados</Typography>
              <Box sx={{ maxHeight: '20vh', overflowY: 'auto', pr: 1 }}>
              {UI_BLOCKS.map((block) => {
                const rows = resultadosPorBloque[block.codigo] || [];

                if (!rows.length) {
                  return null;
                }

                return (
                  <Box key={block.codigo} mb={3}>
                    <Box mb={1}>
                      <Typography variant="subtitle1">{block.nombre}</Typography>
                      {block.restriccion && (
                        <Typography variant="caption" color="warning.main">
                          Restriccion: {block.restriccion}
                        </Typography>
                      )}
                    </Box>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            {block.columnas.map((column) => (
                              <TableCell key={column}>{FIELD_LABELS[column] || column}</TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rows.map((row) => (
                            <TableRow key={row.rowId}>
                              {block.columnas.map((column) => (
                                <TableCell key={`${row.rowId}-${column}`}>
                                  {renderCellField(block.codigo, row, column, block.opciones?.[column])}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                );
              })}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" mb={2}>Resumen final</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Ensayo</TableCell>
                      <TableCell>Identificaciones</TableCell>
                      <TableCell>Resultado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {resumenFinal.map((item) => {
                      const status = getResultStatus(item.resultado);
                      return (
                        <TableRow key={item.id}>
                          <TableCell>{item.ensayo}</TableCell>
                          <TableCell>{item.identificaciones}</TableCell>
                          <TableCell sx={{ color: STATUS_COLORS[status], fontWeight: 700 }}>
                            {item.resultado}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box mt={2} display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleExportarPdf}
                  disabled={!ensayoId}
                  startIcon={<PictureAsPdfIcon />}
                >
                  Exportar PDF
                </Button>
                <Button
                  variant="contained"
                  onClick={handleGuardarResultados}
                  disabled={saving}
                  sx={{ backgroundColor: '#cedc00', color: '#000000' }}
                >
                  {saving ? 'Guardando...' : 'Guardar y cerrar ensayo'}
                </Button>
              </Box>
            </Paper>
          </Grid>
        )}

      </Grid>

      <ModalCliente
        open={openModalCliente}
        onClose={() => setOpenModalCliente(false)}
        onSelect={(cliente) => {
          setSelectedCliente(cliente);
          setOpenModalCliente(false);
        }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LaboratorioResultados;